// The rail row — brief §4.3 item 3 (P31, P44), 2026-09-24. ONE row law for
// every sidebar, declared on the primitive (Rule 65), so no rail restates it:
//
//   - a LINE icon at `iconSm` and a label at 500, `caption` ink at rest;
//   - hover = the `hover` wash + `title` ink; pressed = the `selected` wash;
//   - the ACTIVE row (`isActive`, supplied by the consumer) = the `selected`
//     wash + `title` ink + 700;
//   - an optional right-aligned META count (`meta`) in `inactive`, tabular
//     by the brand's html default;
//   - EXACTLY `controlHCompact` (36) tall, `controlH` (44) on a coarse
//     pointer: no vertical padding, so the min-height alone sets the height
//     (with `Z` padding the row measured 44.88px at a 17px font — the
//     brief's "39px" class of row);
//   - focus = the global ring only. A `selected` wash on :focus-visible made a
//     keyboard-focused row read as the ACTIVE one, so the row paints none.
//
// Contrast (DESIGN_LANGUAGE §4.2, D-2): on the dark `hover` wash `inactive`
// reads 4.29:1 and on the dark `selected` wash 3.33:1 — both under 4.5. So the
// meta ink is a custom property the ROW owns: `inactive` at rest, `caption` on
// hover, pressed and active. The meta child reads it; no child key is nested
// in a state block (never a valid selector) and no node is touched.
//
// The ACTIVE half rides on a `data-active` attribute block, not on
// `.isActive`: a `.isX` block is painted INLINE and that painter drops custom
// properties (measured live 2026-09-24: the active row's style attribute held
// background, color and weight but no --sidebar-row-meta, so the meta stayed
// `inactive` at 3.29:1). An attribute block compiles to a CLASS rule, where a
// custom property survives — the same way the :hover and :active ones do.
//
// Why a new shared primitive: the shell's `SidebarRow` lives in the workspace
// package, which a standalone app (canvas) never loads, and every rail
// restated its active look at the call site. `SidebarRow` folds into
// `extends: 'AppSidebarRow'` as its owning lane's one-liner, the same path
// `AppSidebarGroup` took from `TicketsSidebarGroup`.
export const AppSidebarRow = {
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  minHeight: 'controlHCompact',
  padding: '0 Z1',
  round: 'radiusControl',
  cursor: (el) => (el.onClick ? 'pointer' : 'default'),
  background: 'transparent',
  color: 'caption',
  fontWeight: '500',
  '--sidebar-row-meta': 'var(--color-inactive)',

  // Keyboard-operable once a consumer attaches onClick: role + tabindex make
  // it focusable so the global :focus-visible ring engages, and Enter/Space
  // mirror the click. A key from a nested control (a row menu) stays its own.
  'data-sidebar-row': 'true',
  role: (el) => (el.onClick ? 'button' : null),
  tabindex: (el) => (el.onClick ? '0' : null),
  onKeydown: (e, el, s, ctx) => {
    if (e.target !== el.node || e.defaultPrevented) return
    if ((e.key === 'Enter' || e.key === ' ') && el.onClick) {
      e.preventDefault()
      el.onClick(e, el, s, ctx)
    }
  },

  ':hover': {
    background: 'hover',
    color: 'title',
    '--sidebar-row-meta': 'var(--color-caption)'
  },
  ':active': {
    background: 'selected',
    color: 'title',
    '--sidebar-row-meta': 'var(--color-caption)'
  },
  '.isActive': {
    background: 'selected',
    color: 'title',
    fontWeight: '700'
  },
  'data-active': (el, s) => {
    const active = el.isActive
    return (typeof active === 'function' ? active(el, s) : active) ? 'true' : null
  },
  '[data-active="true"]': { '--sidebar-row-meta': 'var(--color-caption)' },
  '@coarse': { minHeight: 'controlH' },
  '@mobileL': { minHeight: 'controlH' },
  touchAction: 'manipulation',

  // `if:` beside `hide:`, same predicate inverted. A HIDDEN Icon is still a
  // MOUNTED Icon, and the built-in resolves its name from
  // `el.name || el.icon || el.key`: a row naming no glyph answered `undefined`
  // here, fell through to the KEY and warned `[Icon] Unknown icon name "Icon"`
  // while painting the `noIcon` placeholder behind the hide. `hide:` stays as
  // the fallback for a runtime whose `if:` is inert. Same fix as
  // ContextDropdownItem.Icon — see the comment at that slot.
  Icon: {
    if: (el, s) => !!(el.parent?.icon || s?.icon),
    icon: (el, s) => el.parent?.icon || s?.icon,
    hide: (el, s) => !(el.parent?.icon || s?.icon),
    'aria-hidden': true,
    boxSize: 'iconSm',
    flexShrink: '0'
  },

  // A bundle key or a plain string: polyglot returns a non-key verbatim.
  Label: {
    tag: 'span',
    fontSize: 'Z2',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => el.call('polyglot', el.parent?.label ?? s?.label ?? '')
  },

  Meta: {
    tag: 'span',
    fontSize: 'Z',
    fontWeight: '400',
    flexShrink: '0',
    color: 'var(--sidebar-row-meta)',
    text: (el, s) => String(el.parent?.meta ?? s?.meta ?? ''),
    hide: (el, s) => {
      const meta = el.parent?.meta ?? s?.meta
      return meta === undefined || meta === null || meta === ''
    }
  }
}
